import React, {useContext} from 'react'
import {Topic} from '../topic'
import {ExternalSiteUrl} from '../external-site-url'
import {ResultContainer, ResultHeading} from './styles'
import {OntarioBadge} from '@ongov/ontario-design-system-component-library-react'
import {sanitizeAndDecode} from '../../utils/utils'
import {captureGA} from '../../utils/analytics'
import {useTranslation} from 'react-i18next'
import {AppContext} from '../../context/context'

const clickApiUrl = `${process.env.REACT_APP_ENDPOINT_BASE}/api/as/v1/engines/${process.env.REACT_APP_ENGINE_NAME}/click`
const authHeader = `Bearer ${process.env.REACT_APP_SEARCH_KEY}`

const Result = ({searchRecord, query, resultPos, elawsDescriptionsObject}) => {
  // Sanitize results before using them.
  const sanitizedSearchId = sanitizeAndDecode(searchRecord.id.raw)
  const sanitizedSearchUrl = sanitizeAndDecode(searchRecord.url.raw)
  let encodingFormats = ''
  let ldJson = ''

  const {t} = useTranslation('common')
  const {state} = useContext(AppContext)

  const ldJsonValid = sanitizeAndDecode(searchRecord?.linkedMetadata?.raw)
  if (ldJsonValid && !ldJsonValid.toLowerCase().includes('null')) {
    ldJson = JSON.parse(ldJsonValid)
  }

  /**
   * Truncates the left part of a title string if it contains a pipe character ('|')
   * and the left part is longer than the specified max character length.
   *
   * - The title is expected to be in the format: "left side | right side".
   * - If the left side is longer than maxCharsLeft, it truncates it and adds an ellipsis ('...').
   * - The right side is kept intact.
   * - If there is no pipe character in the title, the function returns the title unchanged.
   */
  function truncateIfLongTitle(title, maxCharsLeft = 100) {
    if (!title.includes('|')) return title

    const parts = title.split('|')
    const left = parts[0].trim()
    const right = parts[1].trim()

    if (left.length > maxCharsLeft) {
      const truncatedLeft = `${left.slice(0, maxCharsLeft)}\u2026`
      return `${truncatedLeft} | ${right}`
    }

    return title
  }

  /**
   *
   * It removes all empty strings and checks to see if there are valid formats in the resulting array to be displayed after filtering.
   * This list is memoized in `encodingFormats` to be used in an another function to render them.
   */
  function encodingFormatsNonEmpty() {
    encodingFormats = ldJson.distribution

    if (encodingFormats) {
      encodingFormats = encodingFormats
        .map(el => el.encodingFormat.toUpperCase())
        .filter(Boolean)

      const isNonEmptyFormatList = encodingFormats.some(Boolean)

      return isNonEmptyFormatList
    }
  }

  /**
   * Renders a list of unique encoding formats in a DS badge, in alphabetical order.
   */
  function listEncodingFormats(formatList) {
    const uniqueFormats = [...new Set(formatList)].sort()

    return (
      <span className="resource-format heading-default">
        {t('resourceFor')}
        {uniqueFormats.length > 0
          ? uniqueFormats.map((val, index) => (
              <React.Fragment key={index}>
                <OntarioBadge colour="light-teal">{val}</OntarioBadge>
              </React.Fragment>
            ))
          : ''}
      </span>
    )
  }

  /**
   * eLaws needs a special result formatting to determine the Act Title, to remove the word `under` in some cases, and to have the custom FVH description.
   */
  function eLawsResultOutput(metadata) {
    let elawsTitle = sanitizeAndDecode(metadata?.title?.raw)
    let splitActTitle = sanitizeAndDecode(metadata?.act_title?.raw)
    // By default, the regulation number is empty.
    let elawsRegulationNumber = ''
    // By default, use the meta_description from ES App search query.
    let elawsDescription = sanitizeAndDecode(metadata?.meta_description?.raw)

    // We must split the id on `|` because meta engine id's are formatted as `engine-name|id`
    const resultID = metadata?.id?.raw.split('|')[1]

    // If the ID matches from the App search query and the elaws FVH Api query, make the description show the snippets.
    if (elawsDescriptionsObject && elawsDescriptionsObject[resultID]) {
      elawsDescription = elawsDescriptionsObject[resultID].join('(...)')
    }

    // If the Act Title is in the string for the given title, that means the result is an Act (parent) and
    // therefore should use the simplified name given in the actTitle field. The breadcrumbs should only show
    // `elaws` as well.
    if (
      elawsTitle.includes(splitActTitle) ||
      splitActTitle.includes(elawsTitle)
    ) {
      elawsTitle = splitActTitle
      splitActTitle = null
    } else {
      // Else, we should use the given actTitle but remove `under` from the result.
      splitActTitle = splitActTitle
        .substring(splitActTitle.indexOf(t('elawsTerms.lowercaseUnder')))
        .replace(t('elawsTerms.uppercaseUnder'), '')
        .trim()

      // We split the Regulation Number (RRO, O. Reg, ETC) from the title.
      const elawsTitleSplit = elawsTitle.split(':')
      elawsTitle = elawsTitleSplit[1] ? elawsTitleSplit[1] : elawsTitle
      elawsRegulationNumber = elawsTitleSplit[0]
    }

    return (
      <>
        {/* ResultHeading is the Title and URL */}
        <ResultHeading>
          <div className="elaws-regulation-number">
            {elawsRegulationNumber ? elawsRegulationNumber : ''}
          </div>
          <a
            href={
              sanitizedSearchUrl.includes('://')
                ? sanitizedSearchUrl
                : `${window.location.origin}/${sanitizedSearchUrl.replace(
                    /^\/?/gi,
                    '',
                  )}`
            }
            data-resultPosition={resultPos}
            onClick={handleLinkClick}
            onKeyDown={handleKeyDown}
          >
            {truncateIfLongTitle(elawsTitle)}
          </a>
          {metadata?.popular_page_flag?.raw ? (
            <div className="popular-badge-inline">
              &nbsp;<OntarioBadge>{t('popular')}</OntarioBadge>
            </div>
          ) : (
            ''
          )}
        </ResultHeading>

        {/* Topic / Breadcrumb */}
        <Topic t={t} metadata={splitActTitle} isLawsTopic={true} />

        {/* Description */}
        {/* Because the response from the FVH contains <div> highlighting, we must dangerously set the HTML */}
        <p dangerouslySetInnerHTML={{__html: elawsDescription}} />
      </>
    )
  }

  /**
   * Takes in the metadata from a document and determines if it is from drupal/ontario.ca or an external URL
   */
  function topicOrExternalURL(metadata) {
    // If the searchURL contains a pattern like `https://` and isnt from www.ontario.ca, show the external URL in the topic field.
    if (
      sanitizedSearchUrl.includes('://') &&
      !sanitizedSearchUrl.startsWith('https://www.ontario.ca/')
    ) {
      return <ExternalSiteUrl url={sanitizedSearchUrl} />
      // Else, show the topic field as the drupal metadata.
    } else {
      return (
        <Topic
          t={t}
          metadata={sanitizeAndDecode(metadata?.drupal_metadata?.raw)}
        />
      )
    }
  }

  /**
   * The default result for a document (i.e. unfiltered). This is where the Title, description, and topic/breadcrumb are set.
   */
  function defaultResultOutput(metadata) {
    return (
      <>
        {/* ResultHeading is the Title and URL */}
        <ResultHeading>
          <a
            href={
              sanitizedSearchUrl.includes('://')
                ? sanitizedSearchUrl
                : `${window.location.origin}/${sanitizedSearchUrl.replace(
                    /^\/?/gi,
                    '',
                  )}`
            }
            data-resultPosition={resultPos}
            onClick={handleLinkClick}
            onKeyDown={handleKeyDown}
          >
            {truncateIfLongTitle(sanitizeAndDecode(metadata?.title?.raw)) || ''}
          </a>
          {metadata?.popular_page_flag?.raw ? (
            <div className="popular-badge-inline">
              &nbsp;<OntarioBadge>{t('popular')}</OntarioBadge>
            </div>
          ) : (
            ''
          )}
        </ResultHeading>

        {/* Topic / Breadcrumbs */}
        {topicOrExternalURL(metadata)}

        {/* Description */}
        <p>{sanitizeAndDecode(metadata?.meta_description?.raw)}</p>
      </>
    )
  }

  /**
   * Takes a datestring and langcode, and outputs date in local date format.
   */
  function listLastValidationDate(dateString, langString) {
    const langCode = langString.toUpperCase()
    //JS Date needs time component to work on all browsers
    const validationDate = new Date(`${dateString}T00:00:00`)

    const formatOptions = {
      EN: {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      },
      FR: {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        locale: 'fr-FR',
      },
    }

    // Get the appropriate format options
    const options = formatOptions[langCode]

    //Output label + date
    return (
      <>
        {t('lastValidatedDate')}{' '}
        {validationDate.toLocaleDateString(langCode, {
          month: options.month,
          day: options.day,
          year: options.year,
        })}
      </>
    )
  }

  function handleLinkClick(e) {
    e.preventDefault()

    captureGA(e, state)
    fetch(clickApiUrl, {
      method: 'POST',
      mode: 'cors',
      cache: 'no-cache',
      headers: {
        'Content-Type': 'application/json',
        Authorization: authHeader,
      },
      redirect: 'follow',
      referrerPolicy: 'no-referrer',
      body: JSON.stringify({
        query,
        document_id: sanitizedSearchId,
      }),
    })

    window.location.href = e.target.href
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      handleLinkClick(e)
    }
  }

  return (
    <ResultContainer key={sanitizedSearchId}>
      {/* if the eLaws filter is on, we need to use eLaws formatting for the results. */}
      {state.filter === 'elaws'
        ? eLawsResultOutput(searchRecord)
        : defaultResultOutput(searchRecord)}

      {/* If document metadata contains `Linked Data` Json, specifically encoding formats and temporal data */}
      {ldJson && encodingFormatsNonEmpty() ? (
        <p className="resource-format-wrapper">
          {listEncodingFormats(encodingFormats)}
        </p>
      ) : (
        ''
      )}

      {state.filter === 'data' && ldJson && ldJson?.temporal ? (
        <p className="resource-last-validated-date">
          {listLastValidationDate(ldJson.temporal, state.language)}
        </p>
      ) : (
        ''
      )}
    </ResultContainer>
  )
}

export default Result
